import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { getTenantFromBrowser } from '@/utils/tenant.js';
import logo from '@/assets/logo.svg';

const API_BASE = import.meta.env.VITE_API_URL || '';

const statusVariant = {
  emitida: 'outline',
  en_foja: 'outline',
  recibida: 'default',
  en_reparto: 'warning',
  entregada: 'default',
  retenida: 'danger',
};

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('es-AR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

export default function TrackingPortal() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(codigo || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const resolvedCode = useMemo(() => (codigo ? decodeURIComponent(codigo) : ''), [codigo]);

  useEffect(() => {
    if (!resolvedCode) {
      setData(null);
      return;
    }
    setInput(resolvedCode);
    const controller = new AbortController();

    const fetchTracking = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE}/api/tracking/${encodeURIComponent(resolvedCode)}`, {
          headers: {
            'x-tenant': getTenantFromBrowser(),
          },
          signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok || !payload?.ok) {
          throw new Error(payload?.message || payload?.error || 'No se pudo consultar el envío.');
        }
        setData(payload.data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'No se pudo consultar el envío.');
          setData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();

    return () => controller.abort();
  }, [resolvedCode]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) {
      setError('Ingresá el número de guía para consultar.');
      setData(null);
      return;
    }
    setError('');
    navigate(`/tracking/${encodeURIComponent(value)}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-10 sm:px-6 lg:px-8">
        <header className="flex flex-col items-start gap-6 rounded-3xl bg-white p-6 shadow-sm dark:bg-slate-900">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="flex items-center gap-3">
              <img src={logo} alt="SinamonSoft" className="h-10 w-auto" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-teal">Portal de clientes</p>
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Seguimiento de envíos</h1>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Consultá el estado de tu envío sin ingresar al sistema interno.
            </p>
          </div>
          <form
            onSubmit={handleSubmit}
            className="grid w-full gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40 sm:grid-cols-[1fr_auto]"
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="tracking-code" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Nº de guía o código de seguimiento
              </label>
              <input
                id="tracking-code"
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ej: 00012345"
                className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
            <Button type="submit" size="lg" className="w-full sm:w-auto">
              {loading ? 'Consultando...' : 'Consultar'}
            </Button>
          </form>
          {error ? (
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </p>
          ) : null}
        </header>

        <main className="mt-8 space-y-6">
          {!data && !loading ? (
            <section className="rounded-3xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
              <p className="text-lg font-semibold">Ingresá tu código para ver el estado del envío.</p>
              <p className="mt-2 text-sm">Mostramos solo información segura y relevante para el cliente.</p>
            </section>
          ) : null}

          {data ? (
            <>
              <section className="grid gap-6 rounded-3xl bg-white p-6 shadow-sm dark:bg-slate-900 md:grid-cols-[auto_1fr] md:items-center">
                <div className="flex flex-col items-start gap-3">
                  <Badge
                    variant={statusVariant[data.estado?.key] || 'default'}
                    className="px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em]"
                  >
                    {data.estado?.label || 'En proceso'}
                  </Badge>
                  <p className="text-sm text-slate-500 dark:text-slate-300">
                    Código consultado: <span className="font-semibold text-slate-900 dark:text-slate-100">{data.codigo}</span>
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                      Origen
                    </p>
                    <p className="mt-2 text-lg font-semibold">{data.origen || '—'}</p>
                  </div>
                  <div className="flex items-center justify-center text-2xl text-brand-teal">→</div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                      Destino
                    </p>
                    <p className="mt-2 text-lg font-semibold">{data.destino || '—'}</p>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                    Fecha de emisión
                  </p>
                  <p className="mt-2 text-base font-semibold text-slate-900 dark:text-slate-100">
                    {formatDate(data.fecha_emision)}
                  </p>
                </div>
              </section>

              <section className="rounded-3xl bg-white p-6 shadow-sm dark:bg-slate-900">
                <h2 className="text-lg font-semibold">Timeline del envío</h2>
                <div className="mt-6 space-y-4">
                  {data.timeline?.length ? (
                    data.timeline.map((item) => (
                      <div key={`${item.key}-${item.fecha}`} className="flex gap-4">
                        <div className="mt-1 h-3 w-3 rounded-full bg-brand-teal"></div>
                        <div>
                          <p className="text-base font-semibold">{item.label}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{formatDate(item.fecha)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">Aún no hay eventos cargados.</p>
                  )}
                </div>
              </section>

              <section className="rounded-3xl bg-white p-6 shadow-sm dark:bg-slate-900">
                <h2 className="text-lg font-semibold">Prueba de entrega</h2>
                {data.prueba_entrega ? (
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                        Receptor
                      </p>
                      <p className="mt-2 text-base font-semibold">{data.prueba_entrega.receptor || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                        Fecha / hora
                      </p>
                      <p className="mt-2 text-base font-semibold">{formatDate(data.prueba_entrega.fecha)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                        Estado
                      </p>
                      <p className="mt-2 text-base font-semibold">
                        {data.prueba_entrega.estado
                          ? (data.prueba_entrega.estado || '').toUpperCase()
                          : '—'}
                      </p>
                    </div>
                    {data.prueba_entrega.motivo ? (
                      <div className="md:col-span-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                          Motivo de retención
                        </p>
                        <p className="mt-2 text-base font-semibold">{data.prueba_entrega.motivo}</p>
                      </div>
                    ) : null}
                    {data.prueba_entrega.fotos?.length ? (
                      <div className="md:col-span-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                          Fotos
                        </p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {data.prueba_entrega.fotos.map((foto, index) => (
                            <div
                              key={`${foto}-${index}`}
                              className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40"
                            >
                              <img src={foto} alt={`Prueba ${index + 1}`} className="h-40 w-full object-cover" />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                    La prueba de entrega se mostrará cuando el envío esté finalizado.
                  </p>
                )}
              </section>
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
}
